import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button, Select, Textarea, Toggle } from '../../components/ui';
import { useListaVehiculos, useListaOperadores, useListaMaquinas } from '../../hooks/useReferencias';
import ordenesCargaCombustibleService from '../../services/ordenesCargaCombustibleService';
import { guardarBorradorSolicitud, limpiarBorradorSolicitud } from '../../utils/ordenCargaDraftStorage';
import type { OrdenCargaCombustibleSolicitarRequest } from '../../types/ordenesCargaCombustible';

export const SolicitarCargaMovilView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [incluirVehiculo, setIncluirVehiculo] = useState(true);
  const [incluirMaquina, setIncluirMaquina] = useState(false);
  const [operadorId, setOperadorId] = useState<number | ''>('');
  const [vehiculoId, setVehiculoId] = useState<number | ''>('');
  const [maquinaId, setMaquinaId] = useState<number | ''>('');
  const [cantidadVehiculo, setCantidadVehiculo] = useState('');
  const [cantidadMaquina, setCantidadMaquina] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [enviando, setEnviando] = useState(false);

  const { data: vehiculos } = useListaVehiculos();
  const { data: maquinas } = useListaMaquinas();
  const { data: operadores } = useListaOperadores();

  const esValido = !!operadorId && (incluirVehiculo || incluirMaquina) && (
    (!incluirVehiculo || !!vehiculoId) &&
    (!incluirMaquina || !!maquinaId)
  );

  const handleEnviar = async () => {
    if (!esValido || !operadorId) return;

    const datos: OrdenCargaCombustibleSolicitarRequest = {
      operador_id: Number(operadorId),
      destinos: [
        ...(incluirVehiculo ? [{
          vehiculo_id: Number(vehiculoId),
          cantidad_solicitada: cantidadVehiculo ? Number(cantidadVehiculo.replace(',', '.')) : undefined,
        }] : []),
        ...(incluirMaquina ? [{
          maquina_id: Number(maquinaId),
          cantidad_solicitada: cantidadMaquina ? Number(cantidadMaquina.replace(',', '.')) : undefined,
        }] : []),
      ],
      observaciones_solicitud: observaciones || undefined,
    };

    setEnviando(true);
    // Borrador local: si se pierde cobertura justo al enviar, esto sobrevive
    // para poder recuperarlo (ver OrdenCargaBorradorBanner).
    guardarBorradorSolicitud(datos);
    try {
      await ordenesCargaCombustibleService.solicitar(datos);
      limpiarBorradorSolicitud();
      toast.success(t('ordenes_carga.messages.solicitada'));
      navigate('/ordenes-carga-combustible');
    } catch (error: any) {
      // El borrador queda guardado — no se limpia en el catch.
      toast.error(error?.response?.data?.detail || error?.message || t('ordenes_carga.messages.error_solicitar'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Header sticky */}
      <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10 flex items-center">
        <button onClick={() => navigate(-1)} className="mr-3 text-gray-600 hover:text-gray-900">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-bold text-gray-900">{t('ordenes_carga.solicitar.title')}</h1>
      </div>

      {/* Contenido */}
      <div className="p-4 space-y-4">
        <Select
          label={t('ordenes_carga.form.operador')}
          value={operadorId}
          onChange={(e) => setOperadorId(e.target.value ? Number(e.target.value) : '')}
          options={[
            { value: '', label: t('common.select') },
            ...(operadores || []).map((o: any) => ({ value: o.id, label: `${o.codigo} - ${o.descripcion}` })),
          ]}
          required
        />

        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-3">
          <Toggle
            checked={incluirVehiculo}
            onChange={setIncluirVehiculo}
            label={t('ordenes_carga.solicitar.incluir_vehiculo')}
          />
          {incluirVehiculo && (
            <div className="space-y-3 pt-1">
              <Select
                label={t('ordenes_carga.form.vehiculo')}
                value={vehiculoId}
                onChange={(e) => setVehiculoId(e.target.value ? Number(e.target.value) : '')}
                options={[
                  { value: '', label: t('common.select') },
                  ...(vehiculos || []).map(v => ({ value: v.id, label: `${v.codigo} - ${v.patente}` })),
                ]}
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('ordenes_carga.solicitar.cantidad_deseada')}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.0"
                  value={cantidadVehiculo}
                  onChange={(e) => setCantidadVehiculo(e.target.value.replace(/[^0-9.,]/g, ''))}
                  className="block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
                />
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-3">
          <Toggle
            checked={incluirMaquina}
            onChange={setIncluirMaquina}
            label={t('ordenes_carga.solicitar.incluir_maquina')}
          />
          {incluirMaquina && (
            <div className="space-y-3 pt-1">
              <Select
                label={t('ordenes_carga.table.maquina')}
                value={maquinaId}
                onChange={(e) => setMaquinaId(e.target.value ? Number(e.target.value) : '')}
                options={[
                  { value: '', label: t('common.select') },
                  ...(maquinas || []).map((m: any) => ({ value: m.id, label: `${m.codigo} - ${m.descripcion || ''}` })),
                ]}
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('ordenes_carga.solicitar.cantidad_deseada')}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.0"
                  value={cantidadMaquina}
                  onChange={(e) => setCantidadMaquina(e.target.value.replace(/[^0-9.,]/g, ''))}
                  className="block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
                />
              </div>
            </div>
          )}
        </div>

        <Textarea
          label={t('ordenes_carga.solicitar.observaciones')}
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={3}
        />
      </div>

      {/* Footer fijo, encima del MobileNavigation */}
      <div className="fixed bottom-20 left-0 right-0 bg-white border-t border-gray-200 p-4 z-[9999] flex gap-3 shadow-lg safe-area-bottom">
        <Button variant="secondary" fullWidth onClick={() => navigate(-1)} disabled={enviando}>
          {t('common.cancel')}
        </Button>
        <Button fullWidth onClick={handleEnviar} disabled={!esValido} loading={enviando}>
          {t('ordenes_carga.solicitar.confirmar')}
        </Button>
      </div>
    </div>
  );
};

export default SolicitarCargaMovilView;
